import React, { useMemo, useState, useEffect, useRef } from "react";
import { Table, Typography, Checkbox, Tooltip, Tag, Spin } from "antd";
import { useTender } from "../../../contexts/TenderContext";
import { COLORS } from "../../../utils/constants";
import { useBoqVersionHistory, useBoqLatestVersion, useBoqSelectedVersion } from "../../../hooks/useBillOfQuantities";

const { Title } = Typography;
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useItemLibrary, buildItemLibraryFromBoqSnapshot } from "../../../hooks/useItemLibrary";
import { useGetRetainingWallsBuildUp } from "../../../hooks/useRetainingWallsBuildUp";
import { useGetGeoToberWallBuildUp } from "../../../hooks/useWallSectionBuildUp";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";
import {
  calculateFoundationsGrandTotal,
  calculateCutFillGrandTotal,
  calculateDrainageGrandTotal,
  calculatePlotDrivesGrandTotal,
  calculatePlotServiceGrandTotal,
  calculatePlotPavingsGrandTotal,
  calculateScaffoldGrandTotal,
  calculateGardenGrandTotal,
  calculateRetScreenWallsGrandTotal,
  calculateGEOToberWallGrandTotal,
} from "../../../utils/summaryCalculations";
import { computeDynamicBillSummaryNodeAmount } from "../../../utils/dynamicBillSummarySerialization";

const { Text } = Typography;

interface SumAnalysisProps {
  recalculateTrigger?: number;
  includeDiscountDisplay?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  includePreliminariesDisplay?: boolean;
  onPreliminariesCheckboxChange?: (checked: boolean) => void;
  includeOhpDisplay?: boolean;
  onOhpCheckboxChange?: (checked: boolean) => void;
  isViewMode?: boolean;
  userRole?: string | null;
}

const SumAnalysis: React.FC<SumAnalysisProps> = ({
  recalculateTrigger = 0,
  includeDiscountDisplay = false,
  onCheckboxChange,
  includePreliminariesDisplay = false,
  onPreliminariesCheckboxChange,
  includeOhpDisplay = false,
  onOhpCheckboxChange,
  isViewMode = false,
  userRole = null,
}) => {
  const { state, dispatch } = useTender();
  const { settings: globalSettings } = useGlobalSettings();
  const [isHistoricVersion, setIsHistoricVersion] = useState<boolean>(
    () => localStorage.getItem("isHistoricalVersion") === "true"
  );

  // Pre-load wall build-up data from API so Sum Analysis doesn't depend on visiting the wall tabs
  const tenderName = state.projectName || "";
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    return raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 0) : 0;
  })()).current;
  // Always refetch from API on mount — the take-off forms save via direct
  // API POSTs that bypass the React Query mutation hook's onSuccess
  // invalidation, so cached specs can be stale and the Sum Analysis total
  // would diverge from the latest take-off / build-up data.
  const { data: retWallsBuildUp } = useGetRetainingWallsBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: geoToberWallBuildUp } = useGetGeoToberWallBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: versionHistoryData } = useBoqVersionHistory(state.projectName || undefined, { enabled: !isHistoricVersion, oppNo: state.opportunityNo });
  const { data: latestVersionData } = useBoqLatestVersion(state.projectName || undefined, { enabled: !isHistoricVersion, oppNo: state.opportunityNo });
  const { data: selectedVersionData } = useBoqSelectedVersion(state.projectName || undefined, { enabled: isHistoricVersion, oppNo: state.opportunityNo });
  const versionData = isHistoricVersion ? selectedVersionData : latestVersionData;

  // Check localStorage for historical version flag (poll for changes)
  useEffect(() => {
    const check = () => setIsHistoricVersion(localStorage.getItem("isHistoricalVersion") === "true");
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, []);

  // For historical versions, derive item library from already-cached BOQ snapshot.
  // For live versions (or view mode with only one version where no snapshot exists),
  // fetch from the item library API. The enabled condition keeps the live query
  // disabled once versionData is loaded (avoids a duplicate getBoqVersion call from
  // useItemLibrary's internal snapshot path for 2+ version projects).
  const { data: liveItemLibraryData } = useItemLibrary(state.region || "", undefined, { enabled: !isHistoricVersion || !versionData }, undefined, state.opportunityNo);
  const historicalItemLibraryData = useMemo(
    () => (isHistoricVersion ? buildItemLibraryFromBoqSnapshot(versionData) : null),
    [isHistoricVersion, versionData],
  );
  // Prefer snapshot derived from versionData; fall back to live when no snapshot is
  // available (single-version view mode where getBoqVersion is correctly skipped).
  const itemLibraryData = isHistoricVersion ? (historicalItemLibraryData ?? liveItemLibraryData) : liveItemLibraryData;

  // Derive BOQ status from cached version data (no extra API calls)
  const apiBoqStatus = useMemo(() => {
    if (!versionData) return null;
    const d = versionData as any;
    return d?.boq?.boqStatus || d?.boq?.status || d?.snapshot?.boqStatus ||
      d?.snapshot?.status || d?.boqStatus || d?.status || null;
  }, [versionData]);

  // Sync derived status into TenderContext
  useEffect(() => {
    if (apiBoqStatus && apiBoqStatus !== state.boqStatus) {
      dispatch({ type: "UPDATE_FIELD", payload: { field: "boqStatus", value: apiBoqStatus } });
    }
  }, [apiBoqStatus, state.boqStatus, dispatch]);

  // Use API status if available, otherwise fallback to state
  const currentBoqStatus = apiBoqStatus || state.boqStatus || "In-progress";

  const ratesMapping = null;

  // Build dynamic sum analysis from actual data
  const processedSumAnalysis = useMemo(() => {
    const sections = [
      { name: "Cut & Fill", stepName: "Cut & Fill" },
      { name: "Foundations", stepName: "Foundations" },
      { name: "Drainage", stepName: "Drainage" },
      { name: "Plot Drives", stepName: "Plot Drives" },
      { name: "Plot Service", stepName: "Plot Service" },
      { name: "Plot Paving", stepName: "Plot Pavings" },
      { name: "Scaffold Platforms", stepName: "Scaffold" },
      { name: "Garden", stepName: "Garden" },
      { name: "Ret & Screen Walls", stepName: "Ret & Screen Walls" },
      { name: "Geowall & Tobermore Walls", stepName: "Geowall & Tobermore Walls" },
    ];

    const items: any[] = [];
    let grandTotal = 0;

    // Compute markup factor at rate level — same as individual bill summaries
    const prelimsPercent = includePreliminariesDisplay ? (globalSettings.groundworks.preliminaries || 0) : 0;
    const ohpPercent = includeOhpDisplay ? (globalSettings.groundworks.overheadAndProfit || 0) : 0;
    const markupFactor = (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);

    // Add each section - show all sections even if they have no data
    sections.forEach((section) => {
      // Calculate grand total using specific calculation for each section,
      // passing markupFactor so prelims/OHP are applied at the rate level
      // (matching how individual bill summaries compute their totals).
      let sectionTotal: number;

      switch (section.stepName) {
        case "Foundations":
          sectionTotal = calculateFoundationsGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Foundations' ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // Cut & Fill's own extra items below.
          if (state.groundworks?.foundationsExtraItems) {
            const foundationsExtraBuckets = state.groundworks.foundationsExtraItems;
            const extraTotal = Object.values(foundationsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Cut & Fill": {
          sectionTotal = calculateCutFillGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Cut & Fill's ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor).
          if (state.groundworks?.cutFillsExtraItems) {
            const cutFillsExtraBuckets = state.groundworks.cutFillsExtraItems;
            const extraTotal = Object.values(cutFillsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        }
        case "Drainage":
          sectionTotal = calculateDrainageGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Drainage's ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // Cut & Fill's/Foundations' own extra items above.
          if (state.groundworks?.drainageExtraItems) {
            const drainageExtraBuckets = state.groundworks.drainageExtraItems;
            const extraTotal = Object.values(drainageExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Plot Drives":
          sectionTotal = calculatePlotDrivesGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Plot Drives' ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // Cut & Fill's/Foundations'/Drainage's own extra items above.
          if (state.groundworks?.plotDrivesExtraItems) {
            const plotDrivesExtraBuckets = state.groundworks.plotDrivesExtraItems;
            const extraTotal = Object.values(plotDrivesExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Plot Service":
          sectionTotal = calculatePlotServiceGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Plot Service's ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // Cut & Fill's/Foundations'/Drainage's/Plot Drives' own extra items above.
          if (state.groundworks?.plotServiceExtraItems) {
            const plotServiceExtraBuckets = state.groundworks.plotServiceExtraItems;
            const extraTotal = Object.values(plotServiceExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Plot Pavings":
          sectionTotal = calculatePlotPavingsGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Plot Pavings' ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // the other groundworks Bill Summaries' own extra items above.
          if (state.groundworks?.plotPavingsExtraItems) {
            const plotPavingsExtraBuckets = state.groundworks.plotPavingsExtraItems;
            const extraTotal = Object.values(plotPavingsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Scaffold":
          sectionTotal = calculateScaffoldGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Scaffold's ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // Cut & Fill's/Foundations'/Drainage's/Plot Drives'/Plot Service's
          // own extra items above.
          if (state.groundworks?.scaffoldExtraItems) {
            const scaffoldExtraBuckets = state.groundworks.scaffoldExtraItems;
            const extraTotal = Object.values(scaffoldExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Garden":
          sectionTotal = calculateGardenGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            markupFactor,
          );
          // Fold Garden's ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // the other groundworks Bill Summaries' own extra items above.
          if (state.groundworks?.gardenExtraItems) {
            const gardenExtraBuckets = state.groundworks.gardenExtraItems;
            const extraTotal = Object.values(gardenExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Ret & Screen Walls":
          sectionTotal = calculateRetScreenWallsGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            retWallsBuildUp?.specifications,
            markupFactor,
          );
          // Fold Ret & Screen Walls' ad-hoc extra items (useBillSummaryExtraItems.tsx)
          // into this same line instead of a separate Sum Analysis row —
          // marked up the same way (markupFactor), mirroring
          // the other groundworks Bill Summaries' own extra items above.
          if (state.groundworks?.retainingWallsExtraItems) {
            const retainingWallsExtraBuckets = state.groundworks.retainingWallsExtraItems;
            const extraTotal = Object.values(retainingWallsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        case "Geowall & Tobermore Walls":
          sectionTotal = calculateGEOToberWallGrandTotal(
            state.groundworks,
            ratesMapping,
            itemLibraryData,
            geoToberWallBuildUp?.specifications,
            markupFactor,
          );
          // Fold Geowall & Tobermore Walls' ad-hoc extra items
          // (useBillSummaryExtraItems.tsx) into this same line instead of a
          // separate Sum Analysis row — marked up the same way (markupFactor), mirroring the extra items
          // feature itself is, mirroring the other groundworks Bill
          // Summaries' own extra items above.
          if (state.groundworks?.geoWallsExtraItems) {
            const geoWallsExtraBuckets = state.groundworks.geoWallsExtraItems;
            const extraTotal = Object.values(geoWallsExtraBuckets).reduce(
              (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
              0,
            );
            sectionTotal += extraTotal;
          }
          break;
        default:
          sectionTotal = 0;
      }

      const itemTotal = sectionTotal;

      items.push({
        Description: section.name,
        Amount: `£${itemTotal.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      });

      grandTotal += itemTotal;
    });

    // Dynamic Bill Summary tabs (user-added "+ Add Tab" sections) each show up
    // as their own Sum Analysis row and roll into the grand total, same as
    // every fixed domain section above.
    (state.groundworks?.dynamicBillSummaryTabs ?? []).forEach((tab) => {
      const tabTotal = tab.nodes.reduce(
        (sum, node) => sum + computeDynamicBillSummaryNodeAmount(node, markupFactor),
        0,
      );
      items.push({
        Description: tab.label,
        Amount: `£${tabTotal.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      });
      grandTotal += tabTotal;
    });

    // OHP and Preliminaries are not added as line items in Sum Analysis

    // Add discount line if discount checkbox is checked and discount > 0
    let finalTotal = grandTotal;
    if (includeDiscountDisplay && globalSettings.groundworks.discount > 0) {
      const discountAmount =
        grandTotal * (globalSettings.groundworks.discount / 100);

      items.push({
        Description: `DISCOUNT (${globalSettings.groundworks.discount}%)`,
        Amount: `-£${discountAmount.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      });

      finalTotal = grandTotal - discountAmount;
    }

    // Add tender total - show final total after discount if applied
    items.push({
      Description: "TENDER TOTAL",
      Amount: `£${finalTotal.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    });

    return items;
  }, [
    state.groundworks,
    globalSettings.groundworks.overheadAndProfit,
    globalSettings.groundworks.preliminaries,
    globalSettings.groundworks.discount,
    includeDiscountDisplay,
    includePreliminariesDisplay,
    includeOhpDisplay,
    recalculateTrigger,
    itemLibraryData,
    retWallsBuildUp,
    geoToberWallBuildUp,
  ]);

  // Process data to create display structure, filtering out rows with £0.00 amount
  const processedData = processedSumAnalysis
    .filter((item) => {
      const description = item.Description || "";
      const isTenderTotal = description === "TENDER TOTAL";
      const isDiscount = description.includes("DISCOUNT (");
      // Always keep tender total and discount rows
      if (isTenderTotal || isDiscount) return true;
      // Filter out rows with zero amount
      return item.Amount !== "£0.00";
    })
    .map((item, index) => {
      const description = item.Description || "";

      // Determine if this is the tender total or discount row
      const isTenderTotal = description === "TENDER TOTAL";
      const isDiscount = description.includes("DISCOUNT (");

      return {
        key: index,
        itemNumber: isTenderTotal || isDiscount ? "" : `${index + 1}.0`,
        description: description,
        amount: item.Amount || "",
        isTenderTotal,
        isDiscount,
      };
    });


  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Title
            level={5}
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "16px",
              color: "#111827",
            }}
          >
            Sum Analysis Bill Summary
          </Title>
          {currentBoqStatus && (
            <Tag
              color={
                isHistoricVersion
                  ? "default"
                  : currentBoqStatus.toLowerCase() === "in-progress"
                    ? "blue"
                    : currentBoqStatus === "Under Review - Senior Estimator"
                      ? "orange"
                      : currentBoqStatus === "Under Review Commercial Director"
                        ? "gold"
                        : currentBoqStatus === "Under Client Review"
                          ? "green"
                          : currentBoqStatus.toLowerCase() === "won"
                            ? "purple"
                            : "default"
              }
              style={{
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              {currentBoqStatus}
            </Tag>
          )}
        </div>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          {onOhpCheckboxChange && (
            <Checkbox
              checked={
                includeOhpDisplay &&
                globalSettings.groundworks.overheadAndProfit > 0
              }
              onChange={(e) => onOhpCheckboxChange(e.target.checked)}
              disabled
            >
              OH&P ({globalSettings.groundworks.overheadAndProfit || 0}%)
            </Checkbox>
          )}
          {onPreliminariesCheckboxChange && (
            <Checkbox
              checked={
                includePreliminariesDisplay &&
                globalSettings.groundworks.preliminaries > 0
              }
              onChange={(e) => onPreliminariesCheckboxChange(e.target.checked)}
              disabled
            >
              Preliminaries ({globalSettings.groundworks.preliminaries || 0}%)
            </Checkbox>
          )}
          {onCheckboxChange && (
            <Checkbox
              checked={
                includeDiscountDisplay &&
                globalSettings.groundworks.discount > 0
              }
              onChange={(e) => onCheckboxChange(e.target.checked)}
              disabled={
                !globalSettings.groundworks.discount ||
                globalSettings.groundworks.discount === 0 ||
                isViewMode ||
                // Disable for Senior Estimator when status is Under Review
                (userRole?.toLowerCase() === "senior estimator" &&
                  currentBoqStatus.toLowerCase().includes("under review"))
              }
            >
              Apply Discount ({globalSettings.groundworks.discount || 0}%)
            </Checkbox>
          )}
        </div>
      </div>

      <style>
        {`
          .sum-analysis-table .ant-table {
            border-radius: 0 !important;
          }
          .sum-analysis-table .ant-table-container {
            border-radius: 0 !important;
          }
          .ant-checkbox-wrapper.ant-checkbox-wrapper-disabled span {
            color: rgba(0, 0, 0, 0.65) !important;
          }
        `}
      </style>
      <Table
        className="sum-analysis-table"
        dataSource={processedData}
        columns={[
          {
            title: "Description",
            dataIndex: "description",
            key: "description",
            render: (value, record) => {
              if (record.isTenderTotal) {
                return (
                  <Text strong style={{ fontSize: "14px", fontWeight: 700 }}>
                    {value}
                  </Text>
                );
              }
              return <Text style={{ fontSize: "14px" }}>{value}</Text>;
            },
          },
          {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            width: 200,
            align: "right",
            render: (value, record) => {
              if (!value) return "";

              if (record.isTenderTotal) {
                return (
                  <Text strong style={{ fontSize: "14px", fontWeight: 700 }}>
                    {value}
                  </Text>
                );
              }

              return (
                <Text strong style={{ fontSize: "14px", fontWeight: 800 }}>
                  {value}
                </Text>
              );
            },
          },
        ]}
        pagination={false}
        size="middle"
        bordered
        rowClassName={(record) => {
          if (record.isTenderTotal || record.isDiscount) {
            return "bg-blue-50";
          }
          return "";
        }}
      />

      <style>{`
        .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </div>
  );
};

export default SumAnalysis;
